/**
 * Locale-aware date/time formatters — thin `Intl` wrappers keyed on the
 * active Lingui locale (callers pass ``i18n.locale``).
 *
 * Moved out of `features/console/lib/format.ts` (which re-exports them)
 * when the chat message meta row needed the same relative label: a
 * timestamp reads the same way in the console and under a message, so
 * it must not be formatted twice.
 */

export function formatDateTime(locale: string, iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function formatDate(locale: string, iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/**
 * Relative timestamp ("2 min fa", "ieri") — ``Intl.RelativeTimeFormat``
 * so pluralization and language come from the active locale (CLDR rules),
 * never from hand-rolled strings. Falls back to "—" on bad input and to
 * the absolute date beyond ~7 days (relative loses meaning there).
 *
 * ``style`` stays ``narrow`` by default — the console's compact chips
 * ("2 min fa") depend on it. The chat meta row asks for ``long``
 * ("2 minuti fa"), which reads as prose under a message.
 */
export function formatRelativeTime(
  locale: string,
  iso: string | null | undefined,
  now: number = Date.now(),
  style: Intl.RelativeTimeFormatStyle = "narrow",
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const time = date.getTime();
  if (Number.isNaN(time)) return "—";

  const deltaSeconds = Math.round((time - now) / 1000);
  const abs = Math.abs(deltaSeconds);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style });
  if (abs < 60) return rtf.format(Math.trunc(deltaSeconds), "second");
  if (abs < 3600) return rtf.format(Math.trunc(deltaSeconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.trunc(deltaSeconds / 3600), "hour");
  if (abs < 604800) return rtf.format(Math.trunc(deltaSeconds / 86400), "day");
  return formatDate(locale, iso);
}
